import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Calgary = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/the-best-neighbourhoods-to-rent-in-calgary-cn/'
    : 'https://liv.rent/blog/neighbourhoods/the-best-neighbourhoods-to-rent-in-calgary/'

  const getAbout = () => {
    return (
      <Trans i18nKey="seo.city.calgary:About Calgary Paragraph 1">
        <Paragraph>
          Calgary, <LinkText href="/rental-listings/alberta">Alberta</LinkText>,
          is a lively, cosmopolitan town with a wide variety of vibrant
          neighbourhoods, an extensive trail system, and easy access to
          world-class skiing and outdoor adventure in nearby Canmore or Banff.
          Though a modern, progressive city, its historic western roots run deep
          and every July the city hosts the world-famous Calgary Stampede - a
          massive rodeo and festival that celebrates the city. Calgary is
          divided into 4 quadrants – the Southwest, Northwest, Southeast,
          Northeast. Live in the revitalized, trendy downtown Beltline
          community, Northwest’s Arbour Lake neighbourhood, or the more suburban
          Chinook Park in the Southwest along with many other{' '}
          <LinkText href={neighborhoodsLink}>
            neighbourhoods available for rent in Calgary
          </LinkText>{' '}
          that cater to everyone’s needs.
        </Paragraph>
      </Trans>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,500 - $2,500',
          bed2: '$1,800 - $2,900',
          bed3: '$2,200 - $3,600'
        }}
        rentReportCity={t('cities:city_name_calgary')}
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.calgary:Apartment Hunting in Calgary Paragraph 1">
            Calgary is an attractive city of Alberta known for its thriving
            community and diverse housing options. While rental prices in
            Calgary are generally more affordable compared to major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and <LinkText href="/rental-listing/city/toronto">Toronto</LinkText>
            , they have been steadily increasing over time. As a result, finding{' '}
            <LinkText href={links.apartment}>cheap rentals in Calgary</LinkText>
            , especially{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>
            , is more challenging than it used to be. However, you can
            potentially lower the rent price through effective negotiation. For
            helpful tips on how to negotiate rent, refer to our guide on{' '}
            <LinkText href="https://liv.rent/blog/renters/how-to-negotiate-rent/">
              how to negotiate rent
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.calgary:Apartments for rent Calgary Paragraph">
              Apartments are one of the most common form of housing you’ll find
              in Calgary. Renters should be prepared for higher prices for{' '}
              <LinkText href={links.apartment}>
                apartments for rent in Calgary
              </LinkText>
              , especially compared to other cities. Still, diligent renters
              should be able to find something within their budget in this
              sprawling metropolis.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.calgary:Houses for rent Calgary Paragraph">
              Though the city is very densely populated, there are quieter areas
              of Calgary surrounding the city’s downtown where renters may find{' '}
              <LinkText href={links.house}>houses for rent in Calgary</LinkText>
              . Both partial houses and entire houses for rent are common, with
              a mix of older and newer homes depending on which part of the city
              you’re looking in.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.calgary:Basements for rent Alberta Paragraph">
              A popular alternative for students,{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are
              basements for rent in {{ cityName }}. Since rent prices for
              apartments and houses can often get too expensive, many renters
              seek out basements for rent in {{ cityName }} as a cheaper
              alternative. Homeowners often rent out their basements as a way to
              offset their mortgage payments. This makes it easy for renters to
              find relatively inexpensive basements for rent in all areas of the
              city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.calgary:Condos for rent Alberta Paragraph">
              Particularly in more densely-populated areas like Downtown{' '}
              {{ cityName }},{' '}
              <LinkText href={links.apartment}>
                condos for rent in {{ cityName }}
              </LinkText>{' '}
              are often the most common form of rental housing. These types of
              rental accommodation are perfect for those who like to be in the
              centre of the action, and don’t need too much space. If you are
              looking to rent in the city centre though, be prepared for
              significantly higher monthly rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Short term rentals CITY Paragraph">
              {{ cityName }} has plenty of work opportunities and world-class
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in {{ cityName }}
              </LinkText>{' '}
              are exceedingly popular, particularly during the summer months and
              at the beginning of school semesters. The added flexibility is an
              appealing prospect for many renters, and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.calgary:Cheap rentals Calgary Paragraph">
              As more people move to Calgary, its rent prices are rising, which
              may make it harder to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Calgary, AB
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in Calgary where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.calgary:Living in Calgary Paragraph">
          Calgary is a vibrant city in{' '}
          <LinkText href="/rental-listings/alberta">Alberta</LinkText>, Canada.
          It offers a high cost of living but has a strong job market. The
          city’s economy is driven by oil and gas, with opportunities in finance
          and technology. Outdoor enthusiasts will love the nearby Rockies for
          activities like hiking and skiing. Calgary has a thriving arts scene,
          with museums and festivals. Education is well-regarded, and the city
          has modern infrastructure. You can refer to our blogs for information
          on{' '}
          <LinkText href={neighborhoodsLink}>
            neighbourhoods in Calgary
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.calgary:Popular Neighbourhoods Calgary Paragraph">
          Like any city, Calgary is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Beltline, Downtown Calgary, The Properties, Skyview Ranch and Mission.
          For more info about Calgary, you can consult this guide on{' '}
          <LinkText href={neighborhoodsLink}>Calgary neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.calgary:Local Landmarks Calgary Paragraph">
          Calgary is the most populous city in Alberta and offers plenty for its
          diverse community of visitors and renters to experience. The Calgary
          Zoo is a great place to take kids and also makes for an excellent way
          for grown-ups to spend an afternoon. If you’re new to the city,
          there’s no better way to experience the city than atop Calgary Tower,
          which offers a revolving restaurant with 360 degree views of the city.
          Of course, during July, the Calgary Stampede becomes the focal point
          of the community and fully embraces the city’s cowboy culture with a
          rodeo, exhibition and festival.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Calgary
